import { useIntl } from 'react-intl';
import moment, { type Moment } from 'moment';
import { useMutation } from '@tanstack/react-query';

import { API } from 'src/reactQuery/Api';
import { queryClient } from 'src/config';
import { putRequest } from 'src/utils/request';
import { forceString } from 'src/utils/datetime';
import { QUERY_KEYS } from 'src/reactQuery/QueryKeys';
import { RequestSimplifiedData } from 'src/types/requestData';
import { useMessageHandler } from 'src/utils/hooks/useMessageHandler';

import messages from '../../messages';

interface CancelContractParams {
  contractUuid: string;
  cancellationSource: string;
  requestedDate: Moment | null;
  endDate: Moment | null;
}

const cancelContract = async ({
  contractUuid,
  endDate,
  requestedDate,
  cancellationSource,
}: CancelContractParams) => {
  const params = {
    user_contract: {
      cancellation_source: cancellationSource,
      end_date: forceString(endDate, moment.HTML5_FMT.DATE),
      requested_date: forceString(requestedDate, moment.HTML5_FMT.DATE),
    },
  };
  return await putRequest<RequestSimplifiedData>(
    API.PUT.cancelContract(contractUuid),
    params,
    { headers: { Accept: 'application/vnd.falcon.v2+json' } },
  );
};

export const useCancelContract = () => {
  const { formatMessage } = useIntl();
  const { handleError, handleSuccess } = useMessageHandler();

  return useMutation({
    mutationFn: cancelContract,
    onSuccess: () => {
      handleSuccess(formatMessage(messages.contractCancelled));
      queryClient.invalidateQueries({
        queryKey: [QUERY_KEYS.CONTRACTS],
      });
    },
    onError: handleError,
  });
};
